import z from "zod";

/**
 * Shared, finite vocabulary for Google Maps cost attribution.
 *
 * Everything in this module is safe to import from both browser and server code:
 * no Node built-ins, no database access.
 *
 * Privacy contract for every dimension defined here: values are compile-time
 * constants chosen by the callsite. Nothing derived from user input (queries,
 * addresses, Place IDs, session tokens, IPs) may ever be stored in these fields.
 */

/** Billable Google web services we call from the server. */
export const MAPS_OUTBOUND_APIS = [
  "geocoding",
  "places_autocomplete",
  "place_details",
] as const;

export type MapsOutboundApi = (typeof MAPS_OUTBOUND_APIS)[number];

/**
 * Browser Dynamic Maps surface. Logged for diagnostics only — Google Cloud is
 * authoritative for Maps JavaScript API / Dynamic Map billing.
 */
export const MAPS_CLIENT_API = "maps_js_dynamic_map" as const;

/** Every `api` value that may appear in `lg_maps_api_usage`. */
export const MAPS_LOG_APIS = [
  ...MAPS_OUTBOUND_APIS,
  MAPS_CLIENT_API,
] as const;

export type MapsLogApi = (typeof MAPS_LOG_APIS)[number];

/**
 * Feature/callsite that caused the call. Every callsite passes its own literal;
 * `unknown` exists only so an un-migrated caller degrades to a visible bucket
 * instead of a null column.
 */
export const MAPS_CALL_SOURCES = [
  /** Paginated result list on `/search`. Resolves location filters server-side. */
  "search_list",
  /** All-matching marker query on `/search`. Resolves location filters server-side. */
  "search_markers",
  /** Place Details / Geocoding used purely to center or bound the search map. */
  "search_map_center",
  /** Autocomplete typed into the main search bar. */
  "search_bar",
  /** Autocomplete / Place Details inside the advanced filter dialog. */
  "filter_dialog",
  /** CMS `?loc=` label resolution to Place IDs. */
  "cms_resolver",
  /** Add/edit location form (Autocomplete, Place Details, address Geocoding). */
  "location_form",
  /** Browser Dynamic Map surface on `/search` (CTA, mount, JS load). */
  "search_map",
  /** Browser Dynamic Map used to adjust coordinates in the advertiser form. */
  "location_form_map",
  "unknown",
] as const;

export type MapsCallSource = (typeof MAPS_CALL_SOURCES)[number];

export const mapsCallSourceSchema = z
  .enum(MAPS_CALL_SOURCES)
  .catch("unknown" as const);

/**
 * Lifecycle stage of one logical Maps request.
 *
 * The first seven values describe server-side web-service calls. The `client_*`
 * values are browser Dynamic Maps telemetry and are never billing records.
 */
export const MAPS_USAGE_STAGES = [
  "cache_hit",
  "cache_miss",
  "deduplicated",
  "google_attempt",
  "google_result",
  "blocked_budget",
  "blocked_rate_limit",
  "blocked_invalid_session",
  "blocked_guard_unavailable",
  "error",
  "client_map_cta_opened",
  "client_map_mounted",
  "client_maps_js_load_ok",
  "client_maps_js_load_error",
] as const;

export type MapsUsageStage = (typeof MAPS_USAGE_STAGES)[number];

/** Stages that represent (or block) a real outbound HTTP request to Google. */
const EXACT_STAGES = new Set<MapsUsageStage>([
  "google_attempt",
  "google_result",
  "blocked_budget",
  "blocked_rate_limit",
  "blocked_invalid_session",
  "blocked_guard_unavailable",
  "error",
]);

/**
 * Outbound-relevant stages are written as one row each so outbound counts stay
 * exact. Everything else (cache hits, dedup, browser telemetry) is aggregated
 * into counted rows to keep routine traffic off the write path.
 */
export function isExactlyLoggedStage(stage: MapsUsageStage): boolean {
  return EXACT_STAGES.has(stage);
}

export const MAPS_CACHE_LAYERS = ["memory", "database", "none"] as const;

export type MapsCacheLayer = (typeof MAPS_CACHE_LAYERS)[number];

/** Browser Dynamic Maps events reported by the client. */
export const MAPS_CLIENT_EVENTS = [
  "map_cta_opened",
  "map_mounted",
  "maps_js_load_ok",
  "maps_js_load_error",
] as const;

export type MapsClientEvent = (typeof MAPS_CLIENT_EVENTS)[number];

const CLIENT_EVENT_STAGES: Record<MapsClientEvent, MapsUsageStage> = {
  map_cta_opened: "client_map_cta_opened",
  map_mounted: "client_map_mounted",
  maps_js_load_ok: "client_maps_js_load_ok",
  maps_js_load_error: "client_maps_js_load_error",
};

export function clientEventToStage(event: MapsClientEvent): MapsUsageStage {
  return CLIENT_EVENT_STAGES[event];
}

/**
 * Legacy `outcome` column values. Kept so the existing superadmin dashboard and
 * any report built on `lg_maps_api_usage.outcome` keeps working unchanged while
 * the richer `stage` / `source` / `cache_layer` dimensions are adopted.
 */
export const MAPS_LEGACY_OUTCOMES = [
  "success",
  "error",
  "zero_results",
  "cache_hit_memory",
  "cache_hit_db",
  "cache_hit_memory_zero_results",
  "cache_hit_db_zero_results",
  "cache_miss",
  "deduplicated",
  "google_attempt",
  "blocked_budget",
  "blocked_rate_limit",
  "blocked_invalid_session",
  "blocked_guard_unavailable",
  "client_event",
] as const;

export type MapsLegacyOutcome = (typeof MAPS_LEGACY_OUTCOMES)[number];

/** Normalizes a release identifier to a short lowercase hex SHA, or null. */
export function normalizeReleaseSha(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim().toLowerCase();
  if (!/^[0-9a-f]{7,40}$/.test(trimmed)) return null;
  return trimmed;
}

export const releaseShaSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[0-9a-f]{7,40}$/)
  .optional();
